import { useState, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../../lib/api.js";
import { StockAdjustDialog } from "./StockAdjustDialog.js";
import { StockHistoryTab } from "./StockHistoryTab.js";

export type WarehouseItem = {
  id: string;
  name: string;
  unit: string;
  quantity: number;
  lowStockThreshold: number;
  updatedAt: string;
};

type Tab = "items" | "history";

export function WarehouseLanding() {
  const [tab, setTab] = useState<Tab>("items");
  const [adjustItem, setAdjustItem] = useState<{
    item: WarehouseItem;
    mode: "in" | "out";
  } | null>(null);
  const [editingThreshold, setEditingThreshold] = useState<{
    id: string;
    value: string;
  } | null>(null);
  // Prevents onBlur from committing when Escape unmounts the input.
  const thresholdCancellingRef = useRef(false);
  const qc = useQueryClient();

  const { data: items = [], isLoading } = useQuery<WarehouseItem[]>({
    queryKey: ["warehouse-items"],
    queryFn: () => api<WarehouseItem[]>("/admin/warehouse/items"),
  });

  const thresholdMutation = useMutation({
    mutationFn: ({
      id,
      lowStockThreshold,
    }: {
      id: string;
      lowStockThreshold: number;
    }) =>
      api(`/admin/warehouse/items/${id}/threshold`, {
        method: "PUT",
        body: JSON.stringify({ lowStockThreshold }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["warehouse-items"] });
      setEditingThreshold(null);
    },
  });

  function commitThreshold(id: string, raw: string) {
    const val = parseInt(raw, 10);
    if (!isNaN(val) && val >= 0) {
      thresholdMutation.mutate({ id, lowStockThreshold: val });
    } else {
      setEditingThreshold(null);
    }
  }

  function formatDate(iso: string) {
    return new Date(iso).toLocaleString("ru-RU", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  return (
    <div data-testid="warehouse-landing" className="content">
      <div className="toolbar">
        <div className="seg">
          <button data-active={tab === "items"} onClick={() => setTab("items")}>
            Остатки
          </button>
          <button
            data-active={tab === "history"}
            onClick={() => setTab("history")}
          >
            История
          </button>
        </div>
      </div>

      {tab === "items" && (
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>Позиция</th>
                <th className="num">Остаток</th>
                <th>Ед.</th>
                <th className="num">Порог</th>
                <th>Изменено</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={6} style={{ padding: 40, textAlign: "center" }}>
                    Загрузка…
                  </td>
                </tr>
              )}
              {items.map((item) => (
                <tr key={item.id} data-testid={`warehouse-row-${item.id}`}>
                  <td>{item.name}</td>
                  <td className="num tnum bold">
                    <span
                      style={{
                        color:
                          item.lowStockThreshold > 0 &&
                          item.quantity <= item.lowStockThreshold
                            ? "var(--mnp-red, #e53e3e)"
                            : undefined,
                      }}
                    >
                      {item.quantity}
                    </span>
                  </td>
                  <td className="muted">{item.unit}</td>
                  <td className="num">
                    {editingThreshold?.id === item.id ? (
                      <input
                        type="number"
                        className="input-admin"
                        style={{ width: 70 }}
                        value={editingThreshold.value}
                        min={0}
                        onChange={(e) =>
                          setEditingThreshold({
                            id: item.id,
                            value: e.target.value,
                          })
                        }
                        onBlur={() => {
                          if (!thresholdCancellingRef.current) {
                            commitThreshold(item.id, editingThreshold.value);
                          }
                          thresholdCancellingRef.current = false;
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter")
                            (e.target as HTMLInputElement).blur();
                          if (e.key === "Escape") {
                            thresholdCancellingRef.current = true;
                            setEditingThreshold(null);
                          }
                        }}
                        autoFocus
                        aria-label="Порог уведомления"
                      />
                    ) : (
                      <button
                        title="Поправить порог"
                        aria-label={`Поправить порог для ${item.name}`}
                        className="btn-admin"
                        style={{ minWidth: 40 }}
                        onClick={() =>
                          setEditingThreshold({
                            id: item.id,
                            value: String(item.lowStockThreshold),
                          })
                        }
                      >
                        {item.lowStockThreshold > 0
                          ? item.lowStockThreshold
                          : "—"}
                      </button>
                    )}
                  </td>
                  <td className="muted" style={{ fontSize: 12 }}>
                    {formatDate(item.updatedAt)}
                  </td>
                  <td>
                    <div style={{ display: "inline-flex", gap: 6 }}>
                      <button
                        className="btn-admin primary sm"
                        aria-label={`Приход ${item.name}`}
                        onClick={() => setAdjustItem({ item, mode: "in" })}
                      >
                        Приход
                      </button>
                      <button
                        className="btn-admin danger sm"
                        aria-label={`Расход ${item.name}`}
                        onClick={() => setAdjustItem({ item, mode: "out" })}
                      >
                        Расход
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "history" && <StockHistoryTab items={items} />}

      {adjustItem && (
        <StockAdjustDialog
          item={adjustItem.item}
          mode={adjustItem.mode}
          onClose={() => setAdjustItem(null)}
          onSuccess={() => {
            qc.invalidateQueries({ queryKey: ["warehouse-items"] });
            qc.invalidateQueries({ queryKey: ["warehouse-log"] });
            setAdjustItem(null);
          }}
        />
      )}
    </div>
  );
}
